import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, List, Spin, Tag, message, Space, Input, Modal } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, QrcodeOutlined, SearchOutlined } from '@ant-design/icons';
import { useAtom, useSetAtom } from 'jotai';
import { loggedInUserAtom, storesAtom } from '../store/userAtom';
import { getStoreById, statisticsStore, visibleStore } from '../api/store';
import Quagga from 'quagga';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [healthStatus, setHealthStatus] = useState({});
  const [messageApi, contextHolder] = message.useMessage();
  const [loggedInUser] = useAtom(loggedInUserAtom);
  const [storeAtom, setStoresAtom] = useAtom(storesAtom)
  const [scanInput, setScanInput] = useState('');
  const [isScanModalVisible, setIsScanModalVisible] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const navigate = useNavigate();

  const fetchStores = async () => {
    if (!loggedInUser?.user_id) {
      messageApi.warning('用户信息加载中，请稍候...');
      return;
    }
    setLoading(true);
    try {
      const response = await visibleStore(loggedInUser.user_id);
      const storesData = response.data || [];
      const statisticsPromises = storesData.map(store => statisticsStore(store.address));
      const statisticsResponses = await Promise.all(statisticsPromises);
      const updatedStores = storesData.map((store, index) => ({
        ...store,
        ...statisticsResponses[index].data
      }));
      setStores(updatedStores);
      setStoresAtom(updatedStores);
      return updatedStores;
    } catch (error) {
      messageApi.error('获取店铺列表失败: ' + (error.response?.data?.message || error.message));
      return [];
    } finally {
      setLoading(false);
    }
  };

  const checkStoreHealth = async (storeId, address) => {
    try {
      setHealthStatus(prev => ({ ...prev, [storeId]: 'checking' }));
      const response = await fetch(`${address}/health`, { method: 'GET', timeout: 5000 });
      setHealthStatus(prev => ({ ...prev, [storeId]: response.ok ? 'online' : 'offline' }));
    } catch (error) {
      setHealthStatus(prev => ({ ...prev, [storeId]: 'offline' }));
    }
  };

  const checkAllStoresHealth = async (storesList) => {
    if (!storesList.length) return;
    const initialStatus = {};
    storesList.forEach(store => initialStatus[store.id] = 'checking');
    setHealthStatus(initialStatus);
    //storesList.forEach(store => checkStoreHealth(store.id, store.address));
  };

  const handleRefresh = async () => {
    const storesList = await fetchStores();
    checkAllStoresHealth(storesList);
  };

  useEffect(() => {
    const init = async () => {
      const storesList = await fetchStores();
      checkAllStoresHealth(storesList);
    };
    if (loggedInUser?.user_id) init();
  }, [loggedInUser?.user_id, messageApi]);

  const getHealthTag = (status) => {
    switch (status) {
      case 'online': return <Tag icon={<CheckCircleOutlined />} color="success">在线</Tag>;
      case 'offline': return <Tag icon={<CloseCircleOutlined />} color="error">离线</Tag>;
      case 'checking': return <Tag color="processing">检查中...</Tag>;
      default: return <Tag color="default">未知</Tag>;
    }
  };

  const startScanner = async () => {
    await new Promise(resolve => setTimeout(resolve, 100));
    if (!scannerRef.current) return;
    Quagga.init({
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: scannerRef.current,
        constraints: { facingMode: "environment" }
      },
      decoder: { readers: ["ean_reader"], debug: { showCanvas: true } }
    }, (err) => {
      if (err) { messageApi.error(`初始化扫码失败: ${err}`); return; }
      Quagga.start();
      setIsScanning(true);
    });

    Quagga.onDetected((result) => {
      if (result && result.codeResult) {
        stopScanner();
        const code = result.codeResult.code;
        setScanInput(code);
        setIsScanModalVisible(false);
        messageApi.success("扫码成功");
        navigate('/product-detail', { state: { code } });
      }
    });
  };

  const stopScanner = () => {
    if (isScanning) { Quagga.stop(); setIsScanning(false); }
  };

  const showScanModal = () => setIsScanModalVisible(true);
  const handleScanInputChange = (e) => setScanInput(e.target.value);

  const handleSearch = () => {
    if (scanInput.trim()) {
      navigate('/product-detail', { state: { code: scanInput.trim() } });
    } else {
      messageApi.warning("请输入搜索内容");
    }
  };

  useEffect(() => () => stopScanner(), [isScanning]);

  useEffect(() => {
    if (isScanModalVisible) startScanner();
    else stopScanner();
  }, [isScanModalVisible]);

  return (
    <div className="home-container" style={{ padding: '20px' }}>
      {contextHolder}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <Input
          placeholder="请输入或扫码"
          value={scanInput}
          onChange={handleScanInputChange}
          style={{ width: '300px' }}
          onPressEnter={handleSearch}
        />
        <Button type="primary" icon={<QrcodeOutlined />} onClick={showScanModal}>扫码</Button>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
      </div>

      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>刷新数据</Button>
      </div>

      <Modal
        title="扫码"
        open={isScanModalVisible}
        onCancel={() => { setIsScanModalVisible(false); stopScanner(); }}
        footer={null}
        width={700}
        destroyOnHidden={true}
      >
        <div ref={scannerRef} style={{ width: '100%', height: '500px', position: 'relative' }}>
          {!isScanning && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>加载中...</div>
          )}
        </div>
      </Modal>

      <Spin spinning={loading} tip="加载中...">
        <List
          grid={{ gutter: 16, column: 1, xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}
          dataSource={stores}
          renderItem={store => (
            <List.Item>
              <Card title={`店铺: ${store.store_name || '未命名'}    状态: 🟢在线`} bordered={true}>
                <Card.Grid style={{ width: '25%', textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>今日营业额</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '8px' }}>¥{store.today_turnover}</div>
                </Card.Grid>
                <Card.Grid style={{ width: '25%', textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>今日交易量</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '8px' }}>{store.today_order_num} 笔</div>
                </Card.Grid>
                <Card.Grid style={{ width: '25%', textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>客单价</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '8px' }}>¥65.5</div>
                </Card.Grid>
                <Card.Grid style={{ width: '25%', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>热销商品</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '8px' }}>商品A</div>
                </Card.Grid>
                <Card.Grid style={{ width: '33.33%', textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>昨日营业额</div>
                  <div style={{ fontSize: '18px', marginTop: '8px' }}>¥{store.yesterday_turnover}</div>
                </Card.Grid>
                <Card.Grid style={{ width: '33.33%', textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>昨日交易量</div>
                  <div style={{ fontSize: '18px', marginTop: '8px' }}>{store.yesterday_order_num} 笔</div>
                </Card.Grid>
                <Card.Grid style={{ width: '33.34%', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>同比增长</div>
                  <div style={{ fontSize: '18px', color: '#3f8600', marginTop: '8px' }}>+26.8%</div>
                </Card.Grid>
              </Card>
            </List.Item>
          )}
        />
        {stores.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '50px' }}><p>暂无店铺数据</p></div>
        )}
      </Spin>
    </div>
  );
};

export default Home;
